import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { toast } from 'react-toastify';
import { adjustMenuPrices } from 'redux/actions/Menu/menuAdvanced';
import BreakGlassDialog from 'components/BreakGlass/BreakGlassDialog';
import { getLoggedInUserDetails, sendBreakGlassLog } from 'utils/breakGlass';

const BREAK_GLASS_PAGE = 'menu-advanced';

const buildActionSummary = ({ increase, percent, round }) =>
  `${increase ? 'Increase' : 'Decrease'} menu prices by ${percent}%${
    round ? ' with round up enabled' : ' without round up'
  }`;

const PriceAdjustSection = ({
  title,
  increase,
  onRequestAction,
  loggedInUserDetails,
}) => {
  const dispatch = useDispatch();
  const [percent, setPercent] = useState('');
  const [roundUp, setRoundUp] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = () => {
    const parsedPercent = parseFloat(percent);
    if (!percent || isNaN(parsedPercent) || parsedPercent <= 0) {
      toast.error('Please enter a valid percent value.');
      return;
    }

    const action = {
      percent: parsedPercent,
      increase,
      round: roundUp,
      summary: buildActionSummary({
        increase,
        percent: parsedPercent,
        round: roundUp,
      }),
    };

    onRequestAction(action, (employeeName) => {
      setLoading(true);
      dispatch(
        adjustMenuPrices({
          percent: action.percent,
          increase: action.increase,
          round: action.round,
        })
      )
        .then(() => {
          sendBreakGlassLog(
            {
              eventType: 'advanced-menu-action',
              employeeName,
              actionSummary: action.summary,
              result: 'success',
              loggedInUser: loggedInUserDetails,
            },
            BREAK_GLASS_PAGE
          );
          toast.success(
            `Prices ${action.increase ? 'increased' : 'decreased'} successfully.`
          );
          setPercent('');
          setRoundUp(false);
        })
        .catch(() => {
          sendBreakGlassLog(
            {
              eventType: 'advanced-menu-action',
              employeeName,
              actionSummary: action.summary,
              result: 'failed',
              loggedInUser: loggedInUserDetails,
            },
            BREAK_GLASS_PAGE
          );
          toast.error(
            `Failed to ${action.increase ? 'increase' : 'decrease'} prices.`
          );
        })
        .finally(() => {
          setLoading(false);
        });
    });
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Percent"
          type="number"
          size="small"
          value={percent}
          onChange={(e) => setPercent(e.target.value)}
          inputProps={{ min: 0, step: 0.01 }}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={roundUp}
              onChange={(e) => setRoundUp(e.target.checked)}
            />
          }
          label="Round Up"
        />
        <Button
          variant="contained"
          color={increase ? 'primary' : 'error'}
          onClick={handleSubmit}
          disabled={loading}
          sx={{ alignSelf: 'flex-start' }}
        >
          {loading ? 'Loading...' : increase ? 'Increase' : 'Decrease'}
        </Button>
      </Box>
    </Paper>
  );
};

const Advanced = () => {
  const loggedInUser = useSelector(({ user }) => user.data);
  const loggedInUserDetails = getLoggedInUserDetails(loggedInUser);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [breakGlassOpen, setBreakGlassOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const handleRequestAction = (action, onConfirm) => {
    setPendingAction({ ...action, onConfirm });
    setConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    setConfirmOpen(false);
    setPendingAction(null);
  };

  const handleConfirmAction = () => {
    setConfirmOpen(false);

    if (!pendingAction) {
      return;
    }

    setBreakGlassOpen(true);
  };

  const handleAuthorized = (employeeName) => {
    if (pendingAction) {
      pendingAction.onConfirm(employeeName);
      setPendingAction(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
        Advanced
      </Typography>
      <Alert severity="warning" sx={{ mb: 3 }}>
        Every advanced action requires break-glass confirmation, employee
        verification, and logging before it can run.
      </Alert>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <PriceAdjustSection
            title="Increase"
            increase={true}
            onRequestAction={handleRequestAction}
            loggedInUserDetails={loggedInUserDetails}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <PriceAdjustSection
            title="Decrease"
            increase={false}
            onRequestAction={handleRequestAction}
            loggedInUserDetails={loggedInUserDetails}
          />
        </Grid>
      </Grid>
      <Dialog
        open={confirmOpen}
        onClose={handleCloseConfirm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Advanced Action</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {pendingAction?.summary}
          </DialogContentText>
          <DialogContentText>
            We will record this action and send the information to management.
            Are you sure you want to do that?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleConfirmAction}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <BreakGlassDialog
        open={breakGlassOpen}
        onClose={() => setBreakGlassOpen(false)}
        onAuthorized={handleAuthorized}
        page={BREAK_GLASS_PAGE}
        actionSummary={pendingAction?.summary}
        description="Enter the employee name and break-glass code to unlock Advanced actions."
      />
    </Box>
  );
};

export default Advanced;
